// src/store/budgets/budgetsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';

const thunk = (name, call) => createAsyncThunk(
  `budgets/${name}`,
  async (arg, { rejectWithValue }) => {
    try {
      return await call(arg);
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const fetchBudgets = thunk('fetchBudgets',
  async (params = {}) => (await apiService.getBudgets(params)).data);
export const createBudget = thunk('createBudget',
  async (data) => (await apiService.createBudget(data)).data);
export const updateBudget = thunk('updateBudget',
  async ({ id, data }) => (await apiService.updateBudget(id, data)).data);
export const deleteBudget = thunk('deleteBudget',
  async (id) => { await apiService.deleteBudget(id); return id; });

const initialState = {
  budgets: [],
  isLoading: false,
  isSaving: false,
  error: null,
  successMessage: null,
};

/** Newest period first, matching the API's ordering, so a newly created row
 *  does not jump to the bottom of the table. */
const byPeriodDesc = (rows) =>
  [...rows].sort((a, b) => (a.period_start < b.period_start ? 1 : -1));

const budgetsSlice = createSlice({
  name: 'budgets',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccessMessage: (state) => { state.successMessage = null; },
  },
  extraReducers: (builder) => {
    const loading = (thunkRef) => {
      builder.addCase(thunkRef.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });
      builder.addCase(thunkRef.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    };
    const saving = (thunkRef, apply, message) => {
      builder.addCase(thunkRef.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      });
      builder.addCase(thunkRef.fulfilled, (state, action) => {
        state.isSaving = false;
        apply(state, action.payload);
        state.successMessage = message;
      });
      builder.addCase(thunkRef.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
    };

    loading(fetchBudgets);
    builder.addCase(fetchBudgets.fulfilled, (state, action) => {
      state.isLoading = false;
      state.budgets = action.payload;
    });

    saving(createBudget, (state, row) => {
      state.budgets = byPeriodDesc([row, ...state.budgets]);
    }, 'Budget saved.');

    saving(updateBudget, (state, row) => {
      state.budgets = state.budgets.map(
        (existing) => (existing.id === row.id ? { ...existing, ...row } : existing));
    }, 'Budget updated.');

    saving(deleteBudget, (state, id) => {
      state.budgets = state.budgets.filter((row) => row.id !== id);
    }, 'Budget removed.');
  },
});

export const { clearError, clearSuccessMessage } = budgetsSlice.actions;
export default budgetsSlice.reducer;
